// frontend/src/pages/CartPage.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import helpers from '../utils/helpers';
import { FaTrash, FaMinus, FaPlus, FaArrowLeft } from 'react-icons/fa';

function CartPage() {
  const { cartItems, updateQuantity, removeFromCart, cartTotal, clearCart, loading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="text-center py-20">Loading cart...</div>;
  }

  if (cartItems.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Your Cart is Empty</h2>
        <p className="text-gray-600 mb-8">Looks like you haven't added any items yet.</p>
        <Link to="/vehicles/browse" className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded-lg transition duration-300">
          Browse Vehicles
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Shopping Cart</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          
          <div className="lg:w-2/3">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-gray-100 border-b border-gray-200 text-sm font-semibold text-gray-600">
                <div className="col-span-6">Product</div>
                <div className="col-span-2 text-center">Price</div>
                <div className="col-span-2 text-center">Quantity</div>
                <div className="col-span-2 text-right">Total</div>
              </div>

              <div className="divide-y divide-gray-200">
                {cartItems.map((item) => {
                  // Check both 'image' (from AI/Vehicles) and 'itemurl' (from Parts)
                  const displayImage = item.image || item.itemurl || 'https://via.placeholder.com/100';
                  
                  // Determine link based on type (if available) or default to parts
                  const linkPath = item.type === 'vehicle' ? `/vehicles/${item.id}` : `/parts/${item.id}`;

                  return (
                    <div key={item.id} className="p-4 flex flex-col md:grid md:grid-cols-12 gap-4 items-center">
                      
                      <div className="col-span-6 flex items-center w-full">
                        <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded overflow-hidden mr-4 border border-gray-200">
                          <img 
                            src={displayImage} 
                            alt={item.itemname} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <Link to={linkPath} className="font-semibold text-gray-800 hover:text-primary line-clamp-2">
                            {item.itemname}
                          </Link>
                          <p className="text-xs text-gray-500 mt-1 capitalize">{item.type || 'Part'}</p>
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 text-xs hover:underline mt-2 flex items-center gap-1 md:hidden"
                          >
                            <FaTrash /> Remove
                          </button>
                        </div>
                      </div>

                      <div className="col-span-2 text-center w-full md:w-auto flex justify-between md:block">
                        <span className="md:hidden text-gray-500 text-sm">Price:</span>
                        <span className="font-medium">{helpers.formatCurrency(item.price, 'NGN')}</span>
                      </div>

                      <div className="col-span-2 flex justify-center items-center w-full md:w-auto py-2 md:py-0">
                        <div className="flex items-center border border-gray-300 rounded">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                            disabled={item.quantity <= 1}
                          >
                            <FaMinus size={10} />
                          </button>
                          <span className="px-2 py-1 text-sm font-medium w-8 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                          >
                            <FaPlus size={10} />
                          </button>
                        </div>
                      </div>

                      <div className="col-span-2 text-right w-full md:w-auto flex justify-between md:block items-center">
                        <span className="md:hidden text-gray-500 text-sm">Subtotal:</span>
                        <div className="font-bold text-gray-800">
                          {helpers.formatCurrency(item.price * item.quantity, 'NGN')}
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="hidden md:inline-block text-red-400 hover:text-red-600 mt-1 text-xs"
                          title="Remove Item"
                        >
                          <FaTrash />
                        </button>
                      </div>

                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="mt-4 flex justify-between">
              <Link to="/vehicles/browse" className="text-primary hover:underline flex items-center gap-2 text-sm">
                <FaArrowLeft /> Continue Shopping
              </Link>
              <button onClick={clearCart} className="text-red-500 hover:text-red-700 text-sm">
                Clear Cart
              </button>
            </div>
          </div>

          <div className="lg:w-1/3">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Order Summary</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal ({cartItems.reduce((acc, item) => acc + item.quantity, 0)} items)</span>
                  <span>{helpers.formatCurrency(cartTotal, 'NGN')}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-sm italic">Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-800">Total</span>
                  <span className="text-2xl font-bold text-primary">{helpers.formatCurrency(cartTotal, 'NGN')}</span>
                </div>
              </div>

              <button 
                className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg transition duration-300 shadow-md"
                onClick={() => navigate('/checkout')}
              >
                Proceed to Checkout
              </button>
              
              <div className="mt-4 text-xs text-gray-500 text-center">
                Secure Checkout powered by Paystack/Flutterwave
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CartPage;